/**
 * @file PoolSpendInputDialog.tsx
 * @description Styled numeric input Dialog for the wizard's
 * pool-spend "Other amount…" sentinel. Replaces the native `prompt()`
 * pattern with a shadcn `Dialog` rooted in the app's theme so the
 * pool-spend custom input matches the rest of the wizard chrome.
 *
 * The dialog enforces the [1, poolMax] range on submit; out-of-range
 * input surfaces inline below the field, not via toast or alert.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export interface PoolSpendInputDialogProps {
  /** When set, dialog is open and tied to this pool size. Setting `null`
   *  closes the dialog. */
  poolMax: number | null;
  /** Called with the chosen integer in [1, poolMax] on confirm. */
  onConfirm: (amount: number) => void;
  /** Called when the user cancels / dismisses / closes the dialog. */
  onCancel: () => void;
}

export default function PoolSpendInputDialog({
  poolMax,
  onConfirm,
  onCancel,
}: PoolSpendInputDialogProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state when the dialog opens for a new pool.
  useEffect(() => {
    if (poolMax !== null) {
      setValue('');
      setError(null);
    }
  }, [poolMax]);

  const handleSubmit = () => {
    if (poolMax === null) return;
    const parsed = Number.parseInt(value.trim(), 10);
    if (!Number.isFinite(parsed)) {
      setError('Enter a number');
      return;
    }
    if (parsed < 1 || parsed > poolMax) {
      setError(`Pick a value from 1 to ${poolMax}`);
      return;
    }
    onConfirm(parsed);
  };

  const open = poolMax !== null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Spend how much?</DialogTitle>
          <DialogDescription>
            {poolMax !== null ? `Pick any value from 1 to ${poolMax} HP.` : null}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input
            ref={inputRef}
            type="number"
            inputMode="numeric"
            min={1}
            max={poolMax ?? undefined}
            value={value}
            placeholder="HP amount"
            autoFocus
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          {error ? (
            <p className="text-xs text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
